import {fixModelUrn} from "./encoding/urn";
import {fetchJson} from "../net/fetch";
import {DtFlatStringStorage} from "../lmv-compat/DtFlatStringStorage";
import {DtExternalRefsStore} from "../lmv-compat/DtExternalRefsStore";
import {KeyFlags, ElementFlags, QC, isStream, ModelSchemaVersionV3} from "./schema/dt-schema";
import {DtConstants} from "./schema/DtConstants";
import {encodeFullId} from "./encoding/elementIdAsFullId";
import {PermissionChecker} from "./PermissionChecker";
import {AttributeDef} from "./schema/Attribute";
import {DtPackage} from "./loader/DtVizLoad";
import {updateSpaceHeightOrElevation} from "./sketchtools/CreateSpace";
import {DT_MODEL_CHANGED_EVENT} from "./DtEventTypes";
import {TandemCategories} from "./schema/TandemCategories";

import { av, THREE } from "@adsk/lmv";

// In theory, this operation could be done by the worker. However, subsequent one is to create fragment data
// which requires instantiation of THREE resources (Vector and Quaternion) which cannot be accessed by the worker since
// it is explicitly disabled via webpack config
import {serializeLmvBufferGeom} from "./worker/geom/OtgGeomEncoder";

import i18n from "i18next";

/** @import { DtLoader } from "./loader/DtLoader" */

const WORKER_UNLOAD_PROPERTYDB = "UNLOAD_PROPERTYDB";
const WORKER_TABLE_SCAN = "TABLE_SCAN";
const WORKER_MUTATE = "MUTATE";
const WORKER_HISTORY = "HISTORY";
const WORKER_GET_HASH_ATTR = "GET_HASH_ATTR";
const WORKER_GET_ATTRIBUTES = "GET_ATTRIBUTES";
const WORKER_RELOAD_ATTRIBUTES = "RELOAD_ATTRIBUTES";
const WORKER_GET_ELEMENT_IDS_FROM_DB_IDS = "GET_ELEMENT_IDS_FROM_DB_IDS";
const WORKER_GET_DB_IDS_FROM_ELEMENT_IDS = "GET_DB_IDS_FROM_ELEMENT_IDS";
const WORKER_GET_PROPERTIES_DT = "GET_PROPERTIES_DT";
const WORKER_GET_CUSTOM_FACETS = "GET_CUSTOM_FACETS";
const WORKER_DELETE_ELEMENT = "DELETE_ELEMENT";
const WORKER_SEARCH_ELEMENTS = "SEARCH_ELEMENTS";
const WORKER_GET_DB_ID_TO_CONNS = "GET_DB_ID_TO_CONNS";
const WORKER_GET_NEW_MEPSYSTEM_KEY = "GET_NEW_MEPSYSTEM_KEY";
const WORKER_DO_REVERT_CHANGE = "REVERT_CHANGE";
const WORKER_GET_SKETCHED_GEOMETRIES = "GET_SKETCHED_GEOMETRIES";
const WORKER_SET_IS_ASSET = "SET_IS_ASSET";

const _tmpBox = new THREE.Box3();
const _tmpRot = new THREE.Quaternion();
const _tmpPos = new THREE.Vector3();
const _tmpScale = new THREE.Vector3();

// TODO-LMV: Last synced with DtModel: 03/10/26
export class DtModel extends av.Model {
	constructor(facility, modelId) {
		// Construct LMV model without parameters, to create an empty model and skip initialization.
		// setData and initialize need to be called by the loader.
		// Note that this will call reset implicitly.
		// All members that need to be reset during model reload, or are used by code in the reset method,
		// should be initialized in the reset method.
		super();

		this.facility = facility;
		this._modelId = fixModelUrn(modelId);
		this.loadContext = facility.loadContext;
		this.lastChangeTime = 0;
		this.subscribed = false;

		// Inherited from LMV (only set during the load process)
		// this.loader = null;
		// this.myData = null;

		this.#reset(true);
	}

	// ----- LMV class overrides -----

	dtor() {
		this.#dispose();

		this.facility = null;
		this.loadContext = null;
		this._loadPromise = null;

		super.dtor();
	}

	/**
	 * Resets a model instance to its post-constructor state.
	 *
	 * This unloads all model data and allows to reload / refill the model from scratch.
	 * Note that Model.initialize needs to be invoked by the caller if required.
	 *
	 * @param {Object|undefined} [modelData]
	 * @param {DtLoader|undefined} [loader]
	 * @param {Boolean} [skipDispose=false]
	 */
	reset(modelData, loader) {
		// We need to invoke this even though the loader is owned by LMV's RenderModel class.
		// But it only drops the loader instance and never calls the dtor.
		this.loader?.dtor();

		super.reset(modelData, loader);
		this.#reset(false);
	}

	#reset(skipDispose) {
		if (!skipDispose) {
			this.#dispose(true);
		}

		// The LMV model sets this to a default object, but we expect it to be null after reset.
		this.myData = null;

		// TODO: This should be uncommented, but that causes an error in dt-client when reloading a model.
		// The old code didn't do this, so we can probably live with this for now.
		// this.invalidateModelProperties();

		this.cbvh = null;
		this.propertyDbError = null;
	}

	#dispose(skipReset = false) {
		this.disposePropertyWorker();

		if (!skipReset) {
			this.#reset(true);
		}
	}

	setData(modelData) {
		super.setModelData(modelData);

		// Tandem model also themes non-graphical elements to allow both the viewer & UIs to query for their themed colors.
		if (modelData) {
			this.myData.dbId2ThemingColor = [];
		}
	}

	getBoundingBox(ignoreTransform, excludeShadow) {
		if (!this.getData()) {
			// LMV's implementation expects model data to exist (even if empty), so it throws if it's undefined.
			// This only affects rare cases that work on newly created models, like model import.
			return null;
		}

		return super.getBoundingBox(ignoreTransform, excludeShadow);
	}

	async search(search) {
		return this.asyncPropertyOperation({
				"operation": WORKER_SEARCH_ELEMENTS,
				"search": search,
				"sourceFileName": this.fileName()
			},
		);
	}

	/**
	 * Computes Bounding box of all fragments, but excluding outliers.
	 * @param {Object} [options]
	 * @param {float}  [options.quantil=0.75]       - in [0,1]. Relative amount of fragments that we consider computation.
	 *                                                By default, we consider the 75% of fragments that are closest to the center.
	 * @param {float}  [options.center]             - Center from which we collect the closest shapes. By default, we use the center of mass.
	 * @param {Array<number>}   [options.allowlist]   - Optional: Fragments to include in fuzzybox, by index.
	 * @param {boolean} [options.useOriginalBounds] - Optional: Use the original (as loaded) fragments world box. Such that changes
	 *                                              - to object position like explode/animation or model matrix are not taken into account.
	 * @returns {THREE.Box3}
	 */
	getFuzzyBox(options = {}) {

		let frags = this.getFragmentList();
		const _tmpBox = new Float32Array(6);

		function getFragBounds(fragId, dstBox) {
			if (options.useOriginalBounds) {
				frags.getOriginalWorldBounds(fragId, _tmpBox);
				dstBox.min.set(_tmpBox[0], _tmpBox[1], _tmpBox[2]);
				dstBox.max.set(_tmpBox[3], _tmpBox[4], _tmpBox[5]);
			} else {
				frags.getWorldBounds(fragId, dstBox);
			}
		}

		function centerOfMass() {

			let box = new THREE.Box3();
			let center = new THREE.Vector3();
			let size = new THREE.Vector3();
			let total = new THREE.Vector3();
			let mass = 0;

			const callback = (fragId) => {
				// get bbox center
				getFragBounds(fragId, box);
				box.getCenter(center);

				// sum centers weighted by bbox size
				let weight = box.size(size).length();
				total.add(center.multiplyScalar(weight));

				mass += weight;
			}

			if (options.allowlist) {
				const list = options.allowlist;
				for (const fragId of list) {
					callback(fragId);
				}
			} else {
				const count = frags.getCount();
				for (let i = 0; i < count; i++) {
					callback(i);
				}
			}

			total.multiplyScalar(1 / mass);
			return total;
		}

		let center = options.center || centerOfMass();
		let quantil = options.quantil || 0.75;

		let fragBox = new THREE.Box3();
		let pt = new THREE.Vector3();

		// Compute distances of each frag bbox from center
		let fragInfos = [];
		const fragCallback = (fragId) => {
			// Skip any empty boxes
			getFragBounds(fragId, fragBox);
			if (fragBox.isEmpty()) {
				return;
			}

			// get fragBox->center distance
			let dist = fragBox.distanceToPoint(center);

			// If fragBox contains the center, use fragBox center.
			if (dist === 0) {
				dist = center.distanceTo(fragBox.getCenter(pt));
			}

			fragInfos.push({
				fragId,
				distance: dist
			});
		};

		if (options.allowlist) {
			const list = options.allowlist;
			for (const fragId of list) {
				fragCallback(fragId);
			}
		} else {
			const count = frags.getCount();
			for (let i = 0; i < count; i++) {
				fragCallback(i);
			}
		}

		// sort by increasing order
		fragInfos.sort(function (a, b) {
			return a.distance - b.distance;
		});

		// union of all fragBoxes, excluding the ones with largest distance to center
		let box = new THREE.Box3();
		for (let i = 0; i < fragInfos.length * quantil; i++) {
			let fi = fragInfos[i];
			getFragBounds(fi.fragId, fragBox);
			box.union(fragBox);
		}
		return box;
	}

	// ----- LMV class overrides end -----
	// ----- Tandem added -----

	initLoadPromise() {
		// if there's a promise that hasn't settled yet, we reuse it
		if(this._loadPromise && !this._loadPromise.settled) {
			return;
		}
		this._loadPromise = {
			settled: false
		};
		// TODO: this can be replaced by Promise.withResolver once supported by nodejs too
		this._loadPromise.promise = new Promise((resolve, reject)=>{
			this._loadPromise.resolve = resolve;
			this._loadPromise.reject = reject;
		});
		this._loadPromise.promise.catch(e=>{
			console.info(`${this.urn()}: ${e}`);
		})
	}

	settleLoadPromise(success, reason) {
		if(!this._loadPromise) {
			console.warn(`Loading of model ${this.urn()} didn't start.`);
			return;
		}

		if(this._loadPromise.settled) {
			return;
		}

		if(success) {
			this._loadPromise.resolve();
		} else {
			this._loadPromise.reject(new Error(reason));
		}
		this._loadPromise.settled = true;
	}

	// This is deliberately different from this.modelRootLoaded (set by LMV in response to the MODEL_ROOT_LOADED_EVENT).
	// MODEL_ROOT_LOADED implies that at least the fragment list is fully loaded, while our _loadPromise is settled
	// earlier, and only guarantees readiness of the actual model root data.
	isModelRootLoaded() {
		return this._loadPromise?.settled ?? false;
	}

	getQueryParams() {
		// e.g. "until=<timestamp>"
		const queryParams = this.loadContext.queryParams;
		// Enforce reading v3 store
		// V3_TODO: this should be done on the server
		if (this.loadContext.noBim && this.facility.getSchemaVersion() === 3) {
			return queryParams ? queryParams + "&v3=true" : "v3=true";
		}
		return queryParams;
	}

	urn() {
		return this._modelId;
	}

	/**
	 * Default model is the one that allows user to create own elements and (potentially) geometries.
	 * It has the same guid as the twin owning the model. Equality of guids is the only indication that model is "default".
	 *
	 * @returns {boolean} True if the model is the default model for the facility.
	 *
	 * @alias Autodesk.Tandem.DtModel#isDefault
	 */
	isDefault() {
		return false;
	}

	/**
	 * Return parent facility of this model.
	 *
	 * @returns {DtFacility} The facility that owns this model.
	 *
	 * @alias Autodesk.Tandem.DtModel#getParentFacility
	 */
	getParentFacility() {
		return this.facility;
	}

	/**
	 * Checks if this model is primary model.
	 *
	 * @returns {boolean}
	 *
	 * @alias Autodesk.Tandem.DtModel#isPrimaryModel
	 */
	isPrimaryModel() {
		return this.facility.getLinkForModel(this)?.main;
	}

	/**
	 * Checks if this model is visible by default.
	 *
	 * @returns {boolean}
	 *
	 * @alias Autodesk.Tandem.DtModel#isVisibleByDefault
	 */
	isVisibleByDefault() {
		return this.facility.getLinkForModel(this)?.on;
	}

	/**
	 * Returns label for this model.
	 *
	 * @returns {string}
	 *
	 * @alias Auodesk.Tandem.DtModel#label
	 */
	label() {
		return this.facility.getLinkForModel(this)?.label;
	}

	/**
	 * Returns access level for this model for current user.
	 *
	 * @returns {string}
	 *
	 * @alias Autodesk.Tandem.DtModel#accessLevel
	 */
	accessLevel() {
		return this.facility.getLinkForModel(this)?.accessLevel;
	}

	_getAccessLevel() {
		return this.accessLevel();
	}

	async updateFromFile(fileName, urn, phaseOrViewName, correlationId, enableIfcV4) {
		let loadContext = {...this.loadContext};
		loadContext.headers['x-dt-span-id'] = this.urn();

		const modelProps = await this.getModelProperties();

		const existingIfcConversionMethod = modelProps.dataSource["ifcConversionVersion"];
		const ifcConversionMethod =
			existingIfcConversionMethod?.length > 0 ? existingIfcConversionMethod : (enableIfcV4 ? "v4" : "v3");

		return fetchJson(loadContext, `/twins/${this.facility.urn()}/import`, 'POST', {
			modelId: this.urn(),
			realFilename: fileName,
			urn: urn,
			phaseOrViewName: phaseOrViewName,
			roomAssignment: true,
			conversionMethod: ifcConversionMethod,
			correlationId: correlationId,
		});
	}

	setPrimaryModel() {
		return this.facility.makePrimary(this);
	}

	setVisibleByDefault(visibleByDefault) {
		return this.facility.updateLinkForModel(this, {
			...this.facility.getLinkForModel(this),
			on: visibleByDefault
		});
	}

	setLabel(label) {
		return this.facility.updateLinkForModel(this, {
			...this.facility.getLinkForModel(this),
			label
		});
	}

	updateFacetInfo(facetInfo) {
		const svf = this.getData();

		Object.assign(svf, facetInfo);

		svf.dbId2UfClass = new DtFlatStringStorage(svf.dbId2UfClass);
		svf.dbId2Class = new DtFlatStringStorage(svf.dbId2Class);

		//Build a reverse map of xref IDs, i.e. given an xref integer ID,
		//it returns the modelID + fully qualified database ID.
		//This is used by the facets manager.
		if (facetInfo.xrefStore) {
			Object.assign(svf, DtExternalRefsStore.materialize(facetInfo.xrefStore));
			delete svf.xrefStore;
		} else {
			svf.xrefId2fullId = {};
		}

		//Post-process the rooms list into a map for easier use by the facet manager
		svf.roomMap = svf.rooms.reduce((acc, cur) => {
			acc[cur.dbId] = cur;
			acc[cur.externalId] = cur;

			cur.model = this;
			return acc;
		}, {});

		svf.levelMap = svf.levels.reduce((acc, cur) => {

			const aecMetadataLevel = svf.levelsAecMetadataMap?.get(cur.originalName);
			cur.height = aecMetadataLevel?.height;

			//For some levels, we get height set to the maximum positive 32 bit integer -- not sure why,
			//but I'm sure Revit must have had a good reason...
			if (cur.height === 0x7fffffff) {
				cur.height = undefined;
			}

			acc[cur.dbId] = cur;
			return acc;
		}, {});

		svf.locationMap = (svf.locations ?? []).reduce((acc, cur) => {
			acc[cur.dbId] = cur;
			acc[cur.externalId] = cur;

			cur.model = this;
			return acc;
		}, {});
	}

	getBasePath() {
		return this.loadContext.endpoint + "/modeldata/" + this.urn() + "/";
	}

	asyncPropertyOperation(opArgs, success, fail) {
		let usePromise = (success === undefined && fail === undefined);

		if (this.propertyDbError) {
			fail && fail(this.propertyDbError);
			return usePromise ? Promise.reject(this.propertyDbError) : undefined;
		}

		let xfer = {...this.loadContext, ...opArgs};

		//Have to reset this which gets inherited by the per-facility loadContext, so that a fresh
		// cbId is automatically assigned for this specific operation.
		xfer.cbId = undefined;
		xfer.dbPath = this.getBasePath();

		xfer.modelId = this.urn();
		xfer.twinId = this.getParentFacility().urn(); //should not be needed after the initial PDB creation happens in load()
		xfer.queryParams = this.getQueryParams();

		xfer.headers = {
			...this.loadContext.headers,
			...opArgs.headers,
		};

		let prom = this.getParentFacility().getWorker(this.id).asyncDatabaseOperation(xfer);

		if (usePromise) {
			return prom;
		} else {
			prom.then(success).catch(fail);
		}
	}

	/**
	 * Queries elements in the model based on specified criteria.
	 *
	 * @typedef {Object} DtModelQuery
	 * @property {number[]} dbIds Array of database IDs to query.
	 * @property {Array<string>} [externalIds] - Array of external element IDs (base64-encoded) to query.
	 *  Converted to dbIds internally. Takes precedence over dbIds if both are provided.
	 * @property {Object} includes
	 * @property {boolean} includes.standard Whether to include standard attributes.
	 * @property {boolean} includes.applied Whether to include applied (user-defined) attributes.
	 * @property {boolean} includes.element Whether to include element source attributes.
	 * @property {boolean} includes.type Whether to include type (family) attributes.
	 * @property {boolean} includes.status Whether to include status attributes.
	 * @property {boolean} [strict] - If true, only returns elements that have all requested properties
	 * @property {import("./CompileFilter").RowFilter} filter Filter criteria for querying properties.
	 * @property {string} classificationId ID of the classification system used by the facility.
	 *  Impacts availability of the corresponding classification attributes.
	 *
	 * @param {DtModelQuery} query - Query parameters for filtering elements.
	 * @returns {Promise<Object>} Query results containing matched elements and their properties
	 *
	 * @example
	 * // Query specific elements with filters
	 * const results = await model.query({
	 *   dbIds: [1, 2, 3],
	 *   includes: {standard: true, element: true},
	 *   filter: {
	 *     volume: {$gt: 100}
	 *   }
	 * });
	 *
	 * @example
	 * // Query elements by external IDs
	 * const results = await model.query({
	 *   externalIds: ['QUFBQUE...', 'QUFBQUI...'],
	 *   includes: {standard: true}
	 * });
	 *
	 * @example
	 * // Query elements with custom properties
	 * const results = await model.query({
	 *   includes: {applied: true, status: true},
	 *   filter: {
	 *     [DtConstants.ColumnFamilies.DtProperties]: {existsInRaw: true}
	 *   }
	 * });
	 *
	 * @alias Autodesk.Tandem.DtModel#query
	 */
	async query(query) {
		// TODO: would be better if this wasn't exposed the the caller at all
		if(!query.hasOwnProperty("classificationId")) {
			query.classificationId = (await this.facility.getClassificationTemplate())?.uuid;
		}
		return this.asyncPropertyOperation({
				"operation": WORKER_TABLE_SCAN,
				"query": query,
				"sourceFileName": this.fileName()
			},
		);
	}

	async _fillTimeSeriesValues(props, dbIds) {
		const multiselect = dbIds.length > 1;
		const dbId = dbIds[0];
		const streamManager = this.facility?.getStreamManager();
		if (streamManager) {
			if (multiselect) {
				// set all timeseries props to "*varies*"
				for (let prop of props.element.properties) {
					if (streamManager.isConnectedAttribute(prop.internalName)) {
						prop.varies = true;
					}
				}

				return props;
			}

			// find DB ids to check time-series data for
			const flags = this.getData().dbId2flags;
			const fetchLastReadingsFor = isStream(this.facility.getSchemaVersion(), flags?.[dbId]) ? dbId : undefined;

			if (fetchLastReadingsFor) {
				const lastReadings = await streamManager.getLastReadings([fetchLastReadingsFor]);

				if (lastReadings[0] && props?.element?.properties) {
					const timeSeriesData = lastReadings[0];
					for (let prop of props.element.properties) {
						const value = timeSeriesData[prop.internalName];
						if (value !== undefined) {
							const {ts, val} = value;
							prop.displayValue = val;
							prop.timestamp = Number.parseInt(ts) * 1000;
						}
					}
				}
			}
		}

		return props;
	}

	async getPropertiesDt(dbIds, options = {}) {
		let res = await this.asyncPropertyOperation({
			"operation": WORKER_GET_PROPERTIES_DT,
			"dbIds": dbIds,
			"options": options
		});

		res.model = this;
		if (this.isDefault() && options.wantTimeSeries) {
			res = await this._fillTimeSeriesValues(res, Array.isArray(dbIds) ? dbIds : [dbIds])
		}

		return res;
	}

	async mutateSketchedSpaceHeight(dbIds, muts, correlationId) {
		if (!this.isDefault()) {
			throw new Error("Height mutation for sketched spaces is only supported for default models");
		}

		let numUpdated = 0;
		let externalIds = [];

		const heightChanged = muts[0][0] === QC.Height; // it is either height or elevation

		// This method mutates the affected elements, so we clear the viewer selection first.
		const selection = this.facility.viewer.getAggregateSelection();
		this.facility.viewer.clearSelection();

		for (let i = 0; i < muts.length; i++) {
			const geomProperties = await this.getSketchedGeometries(dbIds[i]);

			let centerline, elevation, height;
			const newValue = muts[i][1];

			for (const prop of geomProperties.element.properties) {
				if (prop.internalName === QC.Centerline) {
					centerline = prop.displayValue;
				} else if (prop.internalName === QC.Elevation) {
					elevation = prop.displayValue;
				}  else if (prop.internalName === QC.Height) {
					height = prop.displayValue;
				}
			}

			heightChanged ? height = newValue : elevation = newValue;

			const res = await updateSpaceHeightOrElevation(this.facility, dbIds[i], centerline, elevation, height, correlationId, heightChanged);

			numUpdated += res.numUpdated
			if (res.externalId) {
				externalIds.push(res.externalId);
			}
		}

		// Re-select the previously selected elements.
		this.facility.viewer.setAggregateSelection(selection);

		const modelGuid = this.urn().slice(DtConstants.DT_MODEL_URN_PREFIX.length + 1);
		externalIds = externalIds.map(id => encodeFullId(modelGuid, id));
		this.facility.assignRooms(
			this.facility.settings.boundByUpperFloor,
			this.facility.settings.skipRoomBounders,
			externalIds
		);

		return {
			numUpdated
		};
	}

	// base implementation for non-default models
	async refreshStreamConfigsIfNeeded(dbIds, muts) {
		return Promise.resolve();
	}

	async mutate(dbIds, muts, desc, rowIds, skipOverrideMapping = false, correlationId) {
		const event = this.facility.app.waitForEvent(DT_MODEL_CHANGED_EVENT, {
			modelId: this.urn(),
			ctype: DtConstants.ChangeTypes.Mutate,
			isOwn: true
		});

		const req = this.asyncPropertyOperation({
			"operation": WORKER_MUTATE,
			"dbIds": dbIds,
			"muts": muts,
			"desc": desc,
			"rowIds": rowIds,
			"skipOverrideMapping": skipOverrideMapping,
			"correlationId": correlationId,
		});

		const res = await Promise.all([req, event]).then(([reqRes, _]) => reqRes);

		await this.refreshStreamConfigsIfNeeded(dbIds, muts);

		return res;
	}

	/**
	 * Undo the change that happened at the given time (only works for property data changes and references)
	 * TODO: add support for undeleting elements (e.g. streams)
	 *
	 * @param {number[]} dbIds - list of dbIds to revert
	 * @param {number} timestamp - change timestamp in miliseconds
	 * @param {boolean} dryRun - if true, the operation will not be executed, but the result will be returned
	 * @returns {object} returns the result of a undo operation
	 *
	 * @alias Autodesk.Tandem.DtModel#undoChange
	 */
	async undoChange (dbIds, timestamp, dryRun = false, allowNonLatest = false, correlationId) {
		const res = await this.asyncPropertyOperation({
			operation: WORKER_DO_REVERT_CHANGE,
			dbIds,
			timestamp,
			dryRun,
			allowNonLatest,
			correlationId,
		});

		await this.refreshStreamConfigsIfNeeded(dbIds, res.muts);

		return res;
	}

	/**
	 *
	 * @param {object[]} elementsData element data with QC.LmvDbId on each element
	 * @param {string} desc context the update was made in
	 * @param {string} correlationId correlation id for the update
	 * @param {boolean} skipOverrideMapping if true, the override mapping will not be applied and base fields will be used
	 * @returns {Promise<{ serverResponse: *, numUpdated: number }>}
	 */
	async updateElementsWithData(elementsData, desc, correlationId, skipOverrideMapping = false) {

		let muts = [];
		let keys = [];

		for (let i = 0; i < elementsData.length; i++) {
			const elmData = elementsData[i];

			let dbId = elmData[QC.LmvDbId];
			if (!dbId || typeof dbId !== "number") {
				return Promise.reject("Missing element dbId");
			}

			for (let key in elmData) {
				if (key === QC.RowKey || key === QC.LmvDbId) {
					continue;
				}
				muts.push([key, elmData[key]]);
				keys.push(dbId);
			}
		}

		return this.mutate(keys, muts, desc, undefined, skipOverrideMapping, correlationId);
	}

	/**
	 * @typedef ModelAlignment
	 * @type {object}
	 * @property {Array} transform - Array representation of the transformation matrix.
	 * @property {string} checksum - Checksum of the alignment content.
	 */

	/**
	 * Obtains requested model alignment, if present.
	 * @returns {Promise<ModelAlignment|undefined>}
	 */
	async getAlignment() {
		return (await this.getModelProperties()).dataSource?.alignment;
	}

	/**
	 * Persists model alignment, set null to remove.
	 * @param {ModelAlignment|null} alignment
	 */
	async saveAlignment(alignment) {
		const curAlign = (await this.getAlignment());
		try {
			const headers = { etag: curAlign?.checksum || '' };
			await fetchJson(this.loadContext, `/models/${this.urn()}/alignment`, 'PUT', alignment, headers);

			if (alignment) this.modelProps.dataSource.alignment = alignment;
			else delete this.modelProps.dataSource.alignment;

		} catch (e) {
			if (e.status === 412) {
				// etag was out of date, indicating our current alignment is outdated.
				await this._getModelProperties();
				throw new Error('outdated');
			}

			console.warn('Error while persisting model alignment', e, alignment);
			throw new Error(`Error while persisting model alignment for : ${this.urn()}`);
		}
	}

	async getHistory(timestamps, min, max, includeChanges, limit) {
		return this.asyncPropertyOperation({
			"operation": WORKER_HISTORY,
			"timestamps": timestamps,
			"max": max,
			"min": min,
			"limit": limit,
			"includeChanges": includeChanges
		});
	}

	/**
	 * Level is a logical element representing a floor.
	 * @typedef {Object} Level
	 * @property {Number} dbId Element DB ID
	 * @property {Number} elev Level elevation, expressed in the model's distance unit
	 * @property {String} externalId Element ID with flags
	 * @property {DtModel} model Host model
	 * @property {String} name Name
	 * @property {Number} order Synthetic order based on element ID
	 * @property {String} originalName Name as found in the original document
	 */

	/**
	 * Levels by dbId.
	 *
	 * @alias Autodesk.Tandem.DtModel#getLevels
	 * @returns {?Object.<Number, Level>} Levels by dbId.
	 */
	getLevels() {
		return this.getData()?.levelMap;
	}

	/**
	 * Checks if a given id is a level
	 * @param {number} dbId
	 * @returns {boolean}
	 */
	isLevel(dbId) {
		return !!this.getData()?.levelMap?.[dbId];
	}

	/**
	 * Location Breakdown Structure elements by dbId. Mirrors {@link getLevels},
	 * but for {@link ElementFlags.Location} elements collected at load time.
	 *
	 * @returns {?Object.<Number, Object>} Locations by dbId.
	 */
	getLocationsMap() {
		return this.getData()?.locationMap;
	}

	/**
	 * Returns the parent Location dbId of a location element — e.g. a room Location's
	 * parent level Location in the default model — or 0 when there is none.
	 *
	 * The parent link is a same-model {@link QC.Parent} ref.
	 *
	 * @param {Number} dbId
	 * @returns {Number} parent location dbId, or 0
	 */
	getLocationParentDbId(dbId) {
		const locationMap = this.getLocationsMap();
		let parentDbId = locationMap?.[dbId]?.parentDbId ?? 0;

		return (parentDbId && locationMap?.[parentDbId]) ? parentDbId : 0;
	}

	/**
	 * Checks if a given id is a location.
	 * @param {number} dbId
	 * @returns {boolean}
	 */
	isLocation(dbId) {
		return !!this.getData()?.locationMap?.[dbId];
	}

	/**
	 * Returns rooms for this model.
	 *
	 * @returns {Object.<number, object>} Map of room objects by dbId.
	 *
	 * @alias Autodesk.Tandem.DtModel#getRooms
	 */
	getRooms() {
		return this.getData()?.roomMap;
	}

	/**
	 * Checks if given id is a room.
	 *
	 * @param {number} dbId
	 *
	 * @returns {boolean}
	 *
	 * @alias Autodesk.Tandem.DtModel#isRoom
	 */
	isRoom(dbId) {
		return !!this.getData().roomMap?.[dbId];
	}

	/**
	 * Checks if model has rooms.
	 *
	 * @returns {boolean}
	 *
	 * @alias Autodesk.Tandem.DtModel#hasRooms
	 */
	hasRooms() {
		return !!this.getData()?.rooms?.length;
	}

	/**
	 * Returns rooms which contain the given element.
	 *
	 * @param {number} elementId
	 * @param {string} [refModelByUrn]
	 *
	 * @returns {Array.<object>}
	 *
	 * @alias Autodesk.Tandem.DtModel#getRoomsOfElement
	 */
	getRoomsOfElement(elementId, refModelByUrn) {
		const pkg = this.getData();

		const result = [];

		// rooms within the same model
		let roomIds = pkg.dbId2roomIds[elementId];
		if (roomIds) {
			const rooms = this.getRooms();

			if (!(roomIds instanceof Array)) {
				roomIds = [roomIds];
			}
			for (const roomId of roomIds) {
				const room = rooms[roomId];
				if(!room) {
					//console.warn(`Room element ${roomId} not found. Skipping room reference.`);
					continue;
				}
				result.push(room);
			}
		}

		// xref'ed rooms
		let xrefs = pkg.dbId2xroomIds[elementId];
		if (xrefs) {
			let fullIds;
			if (xrefs instanceof Array) {
				fullIds = xrefs.map(xref => pkg.xrefId2fullId[xref]);
			} else if (xrefs > 0) {
				fullIds = [pkg.xrefId2fullId[xrefs]];
			}

			for (const [urn, roomId] of fullIds) {
				if(!(urn in refModelByUrn)) {
					//console.warn(`Model '${urn}' not found. Skipping room reference.`);
					continue;
				}
				const m = refModelByUrn[urn];
				const rooms = m.getRooms();
				if(!rooms) {
					//console.warn(`Model '${urn}' not loaded. Skipping room reference.`);
					continue;
				}

				const room = rooms[roomId];
				if(!room) {
					console.warn(`Room element ${roomId} not found in '${urn}. Skipping room reference.`);
					continue;
				}
				result.push(rooms[roomId]);
			}
		}

		//if(result.length !== (new Set(result)).size) {
		//	throw new Error("duplicate room reference found");
		//}

		return result;
	}

	/**
	 * Returns the location element(s) this element is assigned to.
	 *
	 * @param {number} dbId - dbId of the element
	 * @returns {Array.<object>}
	 *
	 * @alias Autodesk.Tandem.DtModel#getLocationsOfElement
	 */
	getLocationsOfElement(dbId) {
		const pkg = this.getData();

		const result = [];

		// Locations live exclusively in the facility's default model.
		const locationsMap = this.getParentFacility().getDefaultModel().getLocationsMap();

		let xrefs = pkg.dbId2xlocationIds?.[dbId];
		if (xrefs && locationsMap) {
			let fullIds;
			if (xrefs instanceof Array) {
				fullIds = xrefs.map(xref => pkg.xrefId2fullId[xref]);
			} else if (xrefs > 0) {
				fullIds = [pkg.xrefId2fullId[xrefs]];
			}

			for (const [, locationId] of fullIds) {
				const location = locationsMap[locationId];
				if (!location) {
					console.warn(`Location element ${locationId} not found in default model. Skipping location reference.`);
					continue;
				}

				result.push(location);
			}
		}

		// If the element is itself a Location (e.g. a room), include its same-model parent Location (e.g. a level).
		const parentDbId = this.getLocationParentDbId(dbId);
		if (parentDbId) {
			const parent = locationsMap?.[parentDbId];
			if (parent) {
				result.push(parent);
			}
		}

		return result;
	}

	/**
	 * Checks if this model has external references to a model with the given URN.
	 *
	 * @param {string} urn - URN of the model
	 * @returns {boolean} True if this model has external references to a model with the given URN.
	 *
	 * @alias Autodesk.Tandem.DtModel#hasExternalRefs
	 */
	hasExternalRefs(urn) {
		let xrefs = this.getData().xrefs;

		if (!xrefs) {
			return false;
		}

		return !!xrefs[urn];
	}

	async getHash2Attr() {
		let res = await this.asyncPropertyOperation({
			"operation": WORKER_GET_HASH_ATTR,
		});

		let res2 = {};

		//Restore AttributeDef object prototype after transfer through the web worker boundary
		for (let attrId in res) {
			let attrData = res[attrId];
			let withType = Object.create(AttributeDef.prototype);
			res2[attrId] = Object.assign(withType, attrData);
		}

		return res2;
	}

	loadElements(ids) {
		return this.loader.loadFragmentsForElements(ids);
	}

	loadAllElements() {
		this.loader.loadFragmentsForElements(null);
	}

	unloadElements(ids, keepFragments = true) {
		this.loader.unloadFragmentsForElements(ids, keepFragments);
	}

	unloadAllElements() {
		this.loader.unloadFragmentsForElements(null);
	}

	getElementsForLevel(levelDbId) {
		let l2d = this.getData().dbId2levelId;
		let ids = [];

		for (let i = 0; i < l2d.length; i++) {
			if (l2d[i] === levelDbId) {
				ids.push(i);
			}
		}
		return ids;
	}

	loadElementsForLevel(levelDbId) {
		let ids = this.getElementsForLevel(levelDbId);
		this.loadElements(ids);
	}

	loadRoomFragments() {
		let roomDbIds = this.getData().rooms.map((cur) => cur.dbId);

		if (roomDbIds.length > 0) {
			return this.loadElements(roomDbIds);
		}
	}

	async _getModelProperties() {
		this.modelProps = await fetchJson(this.loadContext, `/models/${this.urn()}/props`);
		return this.modelProps;
	}

	/**
	 * @returns Information about the model's source data file and a subset of the original design's properties
	 */
	async getModelProperties() {
		return this.getModelPropertiesPromise || (this.getModelPropertiesPromise = this._getModelProperties());
	}

	invalidateModelProperties() {
		this.getModelPropertiesPromise = this.modelProps = null;
	}

	async setDataSource({fileName, urn, docsProjectId, docsAccountId, sourceAttributeMap}) {
		await fetchJson(this.loadContext, `/models/${this.urn()}/datasource`, 'PUT', {
			fileName,
			urn,
			docsProjectId,
			docsAccountId,
			sourceAttributeMap,
		});

		this.invalidateModelProperties();
	}

	/**
	 * Returns file name of this model.
	 *
	 * @returns {string}
	 *
	 * @alias Autodesk.Tandem.DtModel#fileName
	 */
	fileName() {
		if (this.isDefault()) {
			return i18n.t("(Tandem hosted)");
		}
		return this.modelProps?.dataSource?.fileName;
	}

	/**
	 * Returns user friendly name of this model.
	 *
	 * @returns {string}
	 *
	 * @alias Autodesk.Tandem.DtModel#displayName
	 */
	displayName() {
		// We shouldn't use strings returned by the server directly in the UI (at some point we would have to translate it anyway),
		// therefore check the label here.
		// Looks like we do only have one such string for now. Is it worth to create something like "resources/server_strings.json" file
		// and put it there?
		if (this.isDefault()) {
			return i18n.t("(Tandem hosted)");
		}

		return this.label() || this.fileName();
	}

	/**
	 * @param {boolean} waitForFragments
	 * @param {boolean} waitForInstanceTree
	 */
	async waitForLoad(waitForFragments, waitForInstanceTree) {
		const promises = [undefined, undefined];

		// wait until the loading queue kicked off loading of this model (root loaded)
		if(this._loadPromise) {
			await this._loadPromise.promise;
		} else {
			return Promise.reject(`${this.urn()} Failed to wait for model. Model loading was aborted or did never start.`);
		}

		// Loader.dtor could have been called by the time it gets to this promise
		if (!this.loader) {
			return Promise.reject(`${this.urn()} Failed to wait for model. Missing loader.`);
		}


		if (waitForInstanceTree) {
			promises[1] = new Promise((resolve, reject) => {
				this.loader.getObjectTree(resolve, reject);
			});
		}

		if (waitForFragments) {
			promises[0] = new Promise((resolve, reject) => {
				if (this.loader.fragmentsLoaded) {
					resolve();
				}

				const cb = (e) => {
					if (e.model !== this) {
						return;
					}
					this.loader?.viewer3DImpl.api.removeEventListener(av.MODEL_ROOT_LOADED_EVENT, cb);
					resolve();
				};

				this.loader.viewer3DImpl.api.addEventListener(av.MODEL_ROOT_LOADED_EVENT, cb);
			});
		}
		return Promise.all(promises);
	}

	/**
	 * Instructions for creating buckets while initializing a custom facet.
	 * Results in buckets with lower and upper boundary based on the span @see {@link Bucket}
	 * @typedef {Object} BucketDef
	 * @property {string|number} span - span/interval of the buckets: e.g. 10, 100, "Y", "M", "W"
	 * @property {boolean} [rolling] - if true, the date bucket are rolling, i.e. "last week", "last month", etc.
	 */

	/**
	 * @typedef {Object} Bucket
	 * @property {string} id - id of bucket
	 * @property {string} label - display value
	 * @property {number} lower - lower bound of the bucket's range (inclusive)
	 * @property {number} upper - upper bound of the bucket's range (exclusive)
	 */

	/**
	 *
	 * @param {Array.<string>} attributeHashes
	 * @param {Array.<Array.<Bucket>>} buckets optional user provides buckets
	 * @param {Array.<{ distance: number }>} bucketDefs optional user provides bucket definitions
	 * @param {Array.<boolean>} formatValues if true, passes property values through formatValueWithUnits if applicable
	 * @returns
	 */
	async getCustomFacets(attributeHashes, buckets, bucketDefs, formatValues) {
		return this.asyncPropertyOperation({
			"operation": WORKER_GET_CUSTOM_FACETS,
			attributeHashes,
			buckets,
			bucketDefs,
			formatValues,
		});
	}

	async getCustomFacetsDebounced(attributeHashes, buckets, bucketDefs, formatValues) {
		if(!this.customFacetsQuery) {
			this.customFacetsQuery = {
				"operation": WORKER_GET_CUSTOM_FACETS,
				attributeHashes: [],
				buckets: [],
				bucketDefs: [],
				formatValues: [],
				promiseWithResolvers: Promise.withResolvers()
			};

			setTimeout(()=>{
				const { promiseWithResolvers, ...queuedOp } = this.customFacetsQuery;
				this.customFacetsQuery = null;
				this.asyncPropertyOperation(queuedOp).then(promiseWithResolvers.resolve).catch(promiseWithResolvers.reject);
			}, 0);
		}
		const range = [this.customFacetsQuery.attributeHashes.length, this.customFacetsQuery.attributeHashes.length + attributeHashes.length];
		for(let i=0; i<attributeHashes.length; ++i) {
			this.customFacetsQuery.attributeHashes.push(attributeHashes?.[i]);
			this.customFacetsQuery.buckets.push(buckets?.[i]);
			this.customFacetsQuery.bucketDefs.push(bucketDefs?.[i]);
			this.customFacetsQuery.formatValues.push(formatValues?.[i]);
		}

		return this.customFacetsQuery.promiseWithResolvers.promise.then((res) => {
			// Return only the requested attribute hashes
			return res?.slice(range[0], range[1]);
		});
	}

	async getAttributes(options) {
		return this.asyncPropertyOperation({
			"operation": WORKER_GET_ATTRIBUTES,
			"options": options
		});
	}

	/**
	 * Returns element ids from viewer dbIds.
	 *
	 * @param {Array.<number>} dbIds
	 * @returns {Promise<Array.<string>>}
	 *
	 * @alias Autodesk.Tandem.DtModel#getElementIdsFromDbIds
	 */
	async getElementIdsFromDbIds(dbIds) {
		// Mirror of getDbIdsFromElementIds: the worker just runs
		// autoDbIds.getEncodedElementIdWithFlags(dbId, dbId2flags[dbId]) per id, and both
		// inputs live on the main thread. Non-geometric dbIds aren't in the frag-list-first
		// IdMapper, so they come back null and get batched to the worker.
		const data = this.getData();
		const mapper = data?.autoDbIds;
		const flags = data?.dbId2flags;
		if (!mapper || !flags) {
			return this.asyncPropertyOperation({
				"operation": WORKER_GET_ELEMENT_IDS_FROM_DB_IDS,
				"dbIds": dbIds
			});
		}

		const elementIds = new Array(dbIds.length);
		let missIndices = null;
		for (let i = 0; i < dbIds.length; i++) {
			const elementId = mapper.getEncodedElementIdWithFlags(dbIds[i], flags[dbIds[i]]);
			elementIds[i] = elementId;
			if (elementId == null) {
				(missIndices || (missIndices = [])).push(i);
			}
		}

		if (!missIndices) {
			return elementIds;
		}

		const resolved = await this.asyncPropertyOperation({
			"operation": WORKER_GET_ELEMENT_IDS_FROM_DB_IDS,
			"dbIds": missIndices.map((i) => dbIds[i])
		});
		for (let k = 0; k < missIndices.length; k++) {
			elementIds[missIndices[k]] = resolved[k];
		}
		return elementIds;
	}

	/**
	 * Returns viewer dbIds for the given element ids.
	 *
	 * @param {Array.<string>} elementIds
	 * @returns {Promise<Array.<number>>}
	 *
	 * @alias Autodesk.Tandem.DtModel#getDbIdsFromElementIds
	 */
	async getDbIdsFromElementIds(elementIds) {
		// Resolve on the main thread via the IdMapper instead of using the worker
		// The map is complete once the instance tree has loaded,
		// missing dbIds gets resolved by the worker.
		const mapper = this.getData()?.autoDbIds;
		if (!mapper) {
			return this.asyncPropertyOperation({
				"operation": WORKER_GET_DB_IDS_FROM_ELEMENT_IDS,
				"elementIds": elementIds
			});
		}

		const dbIds = new Array(elementIds.length);
		let missingIdx = null;
		for (let i = 0; i < elementIds.length; i++) {
			const dbId = mapper.getDbIdFromB64ExternalId(elementIds[i]);
			dbIds[i] = dbId;
			if (dbId === undefined) {
				if (!missingIdx) {
					missingIdx = [];
				}
				missingIdx.push(i);
			}
		}

		if (!missingIdx) {
			return dbIds;
		}

		const resolved = await this.asyncPropertyOperation({
			"operation": WORKER_GET_DB_IDS_FROM_ELEMENT_IDS,
			"elementIds": missingIdx.map((i) => elementIds[i])
		});
		for (let i = 0; i < missingIdx.length; i++) {
			dbIds[missingIdx[i]] = resolved[i];
		}
		return dbIds;
	}

	canEdit() {
		switch (this.accessLevel()) {
			case 'ReadWrite':
			case 'Manage':
			case 'Owner':
				return true;
		}
		return false;
	}

	getUsageMetrics(startDate, endDate) {

		if (!startDate) {
			return Promise.reject();
		}

		let query = `start=${startDate}`;
		if (endDate) {
			query += `&end=${endDate}`;
		}

		return fetchJson(this.loadContext, `/admin/models/${this.urn()}/metrics?${query}`);
	}

	isLoaded() {
		return !!this.myData;
	}

	/**
	 * Returns a list of ids of either all physical elements in this model (default)
	 * or of a elements matching a specific ElementFlag (see worker/dt-schema.js line 80+)
	 * @param {ElementFlags} [elementFlag = undefined]
	 * @returns {Array<number>}
	 */
	getElementIds(elementFlag = undefined) {
		let ids = [];
		const dbId2flags = this.getData().dbId2flags;
		for (let id = 1; id < dbId2flags.length; ++id) {
			let flags = dbId2flags[id];
			if (elementFlag === undefined ? !(flags & KeyFlags.Logical) : flags === elementFlag) {
				ids.push(id);
			}
		}
		return ids;
	}

	/**
	 * Returns a list of the dbIds of all currently visible elements
	 */
	getVisibleDbIds() {
		const visibleDbIds = [];
		const it = this.getObjectTree();
		const fl = this.getFragmentList();
		const count = fl.getCount();

		for (let fragId = 0; fragId < count; fragId++) {
			if (!fl.isFragOff(fragId) && fl.isFragVisible(fragId) && !!fl.getGeometry(fragId)) {
				const dbId = fl.getDbIds(fragId);
				if (!it.isNodeHidden(dbId) && !it.isNodeOff(dbId)) {
					visibleDbIds.push(dbId);
				}
			}
		}

		return visibleDbIds;
	}

	subscribeToEvents() {
		if (!this.subscribed) {
			this.getParentFacility().app.subscribeToEvents(this)
			this.subscribed = true;
		}
	}

	unsubscribeFromEvents() {
		if (this.subscribed) {
			this.getParentFacility().app.unsubscribeFromEvents(this);
			this.subscribed = false;
		}
	}

	disposePropertyWorker() {
		this.unsubscribeFromEvents();

		if (this.loadContext) {
			this.asyncPropertyOperation({
				"operation": WORKER_UNLOAD_PROPERTYDB
			}).catch(() => {
			});
		}
	}

	async reloadAttributes() {
		return this.asyncPropertyOperation({
			"operation": WORKER_RELOAD_ATTRIBUTES,
		});
	}

	/**
	 * Checks if a given id is an asset
	 * @param {number} dbId
	 * @returns {boolean}
	 */
	isAsset = function(dbId) {
		return !!this.getData()?.assetIds?.has(dbId);
	}

	/**
	 * Checks if a given id is an asset candidate
	 * @param {number} dbId
	 * @returns {boolean}
	 */
	isAssetCandidate = function(dbId) {
		const flag = this.getData()?.dbId2flags?.[dbId];
		if (flag === undefined) return false;

		const hasValidFlag = flag <= ElementFlags.CompositeParent || flag === ElementFlags.GenericAsset;

		if (this.facility.getSchemaVersion() >= 3) {
			return hasValidFlag && !this.getData()?.dbId2xhostOfAsset[dbId];
		}

		return hasValidFlag;
	}

	async getTaggedAssets() {
		const dbIds = this.getElementIds();
		return this.query({
			dbIds,
			includes: {type: true, applied: true},
			filter: {
				[DtConstants.ColumnFamilies.DtProperties]: {
					existsInRaw: true
				}
			}
		});
	}

	// TODO: refactor! This is used only used by FacetStatus which determines
	// whether an element's custom parameters are partially, completely or not
	// filled at all. It would be way more efficient to filter assets that use
	// a classification with custom parameters mapped instead of calling this
	// function.
	async getClassifiedAssets() {

		const { dbId2flags } = this.myData;

		const classificationMaps = [
			(dbId) => this.myData.dbId2TandemCategory[dbId],
			(dbId) => this.myData.dbId2UfClass.hasById(dbId),
			(dbId) => this.myData.dbId2Class.hasById(dbId)
		];

		const dbIds = [];
		const schemaVersion = this.facility.getSchemaVersion();
		for (let id = 1; id < dbId2flags.length; ++id) {
			// TODO: re-evaluate wether this pre-filtering is worth it given that
			// eventually most assets will have a tandem category
			if (!classificationMaps.some(m => m(id))) continue;

			const flags = dbId2flags[id];
			if (
				(flags & KeyFlags.Logical) === 0 ||
				flags === ElementFlags.Level ||
				isStream(schemaVersion, flags) ||
				flags === ElementFlags.GenericAsset ||
				flags === ElementFlags.Ticket) {
				dbIds.push(id);
			}
		}

		return this.query({
			dbIds,
			includes: {type: true, applied: true},
			strict: true
		});
	}

	isWorldBBoxSet() {
		return this.getData()?.metadata["world bounding box"].minXYZ != null;
	}

	async resetWorldBBoxForDefaultModel() {
		if (!this.isDefault()) {
			return;
		}

		const mainModel = this.facility.getPrimaryModel();
		if (!mainModel) {
			throw new Error("Main model is missing");
		}

		let metadata = this.getData()?.metadata;
		if (!metadata) {
			throw new Error("Metadata for the default model is missing");
		}

		const worldBBox = mainModel.getData().metadata["world bounding box"];
		const distanceUnit = mainModel.getData().metadata["distance unit"];
		const customValues = mainModel.getData().metadata["custom values"];
		const georeference = mainModel.getData().metadata.georeference;

		metadata["world bounding box"] = worldBBox;
		metadata["distance unit"] = distanceUnit;
		metadata["custom values"] = customValues;
		metadata.georeference = georeference;

		await fetchJson(this.loadContext, `/modeldata/${this.urn()}/modelroot`, "POST", metadata);
	}

	async createGeometry(geometry) {
		if (!this.isDefault()) {
			throw new Error("Adding of geometries is only supported on default models");
		}

		const modelId = this.urn();

		const otgGeom = serializeLmvBufferGeom(geometry);

		const res = await fetchJson(this.loadContext, `/modeldata/${modelId}/meshes`, 'POST', otgGeom);
		console.log(res);
		return res
	}

	async getSketchedGeometries(dbIds, options = {}) {
		if (!this.isDefault()) {
			throw new Error("Adding of geometries is only supported on default models");
		}

		let res = await this.asyncPropertyOperation({
			"operation": WORKER_GET_SKETCHED_GEOMETRIES,
			"dbIds": dbIds || this.getElementIds(),
			"options": options
		});

		return res;
	}

	/**
	 * @param {string[]}  - a list of element keys -- fullId with element flag
	 */
	async deleteElements(elmKeys, desc, correlationId) {
		return this.asyncPropertyOperation({
			"operation": WORKER_DELETE_ELEMENT,
			"data": elmKeys,
			desc,
			correlationId
		});
	}

	/**
	 * Sets the isAsset flag for the specified elements.
	 *
	 * @param {number[]} dbIds - Array of database IDs to mark as assets.
	 * @param {string} [correlationId] - Optional correlation ID for tracking the operation.
	 * @returns {Promise<void>}
	 *
	 * @alias Autodesk.Tandem.DtModel#makeAssets
	 */
	async makeAssets(dbIds, correlationId) {
		return this.asyncPropertyOperation({
			"operation": WORKER_SET_IS_ASSET,
			"dbIds": dbIds,
			correlationId
		});
	}

	getElementCount() {
		return this.myData.dbId2flags.length;
	}

	getElementUfClass(dbId) {
		return this.myData.dbId2UfClass.getById(dbId);
	}

	getElementCustomClass(dbId) {
		return this.myData.dbId2Class.getById(dbId);
	}

	getElementTandemCategory(dbId) {
		return TandemCategories.index2code(this.myData.dbId2TandemCategory[dbId]);
	}

	/**
	 * Load the meta data from the specified model in the facility.
	 * @param {DtModel} model the specified model object.
	 */
	async loadMetaData() {
		return new Promise((resolve, reject) => {
			let modelRootUrl = this.loadContext.endpoint + "/modeldata/" + this.urn() + "/model";

			DtPackage.loadAsyncResource(this.loadContext, modelRootUrl, "json", function (data) {
				resolve(data);
			}, reject);
		});
	}

	/**
	 * Fetches the mapping of dbId to connections, both internally and externally
	 * @param forceReload fetches data from the server bypassing cache
	 * @returns {Array} the internal and external mappings in an array of format [dbId2XConns, resolvedRefs] where
	 * dbId2XConns is of format {dbId: [urn, ref], ...} and resolvedRefs is {dbId: ref}
	 */
	async getDbIdToConns(dbIds, systemID, forceReload) {
		if (!systemID || !systemID.length) {
			throw new Error("Missing System ID");
		}

		return await this.asyncPropertyOperation({
			"operation": WORKER_GET_DB_ID_TO_CONNS,
			"dbIds": dbIds || this.getElementIds(),
			systemID,
			forceReload
		});
	}

	getAllSystemClasses() {
		const data = this.getData();
		if (!data) {
			return null;
		}

		return data.systemClasses;
	}

	/**
	 * Returns a bounding box containing every fragment of this element.
	 * @param {number} dbId Element dbId
	 * @param {THREE.Box3} dstBounds Optional destination box for bounds.
	 * @return {THREE.Box3} Element's Bounds
	 */
	getElementBounds(dbId,  dstBounds = new THREE.Box3()) {
		const it = this.getObjectTree();
		const fragList = this.getFragmentList();

		dstBounds.makeEmpty();
		it.enumNodeFragments(
			dbId,
			(fragId) => {
				fragList.getWorldBounds(fragId, _tmpBox);
				dstBounds.union(_tmpBox);
			},
			true);

		return dstBounds;
	}

	/**
	 * @throws Non default model
	 * @returns {string} Next increment of a logical system element key
	 */
	async getNewSystemElementKey() {

		if (!this.isDefault()) {
			throw new Error('Invalid operation: MEPSystems are only stored in the default model.');
		}

		return this.asyncPropertyOperation({ operation: WORKER_GET_NEW_MEPSYSTEM_KEY });
	}

	/**
	 * @throws Non default model
	 * @returns {*} Object containing all non-deleted logical system elements, keyed by SystemID
	 */
	async getLogicalSystemElements() {

		if (!this.isDefault()) {
			throw new Error('Invalid operation: MEPSystems are only stored in the default model.');
		}

		return this.getData().systems;
	}

	/** Creates a set of proxy mesh for the node. Useful when adding an element to an overlay. */
	getProxyMeshes(dbId, overrideMaterial) {
		const tree = this.getObjectTree();
		const fragments = this.getFragmentList();

		if (!tree || !fragments) {
			console.log('No meshes as model is not yet loaded');
			return [];
		}

		const meshes = [];
		tree.enumNodeFragments(dbId, (fragId) => {
			const mesh = fragments.getVizmesh(fragId, false);
			if (!mesh.geometry) {
				console.log('Geometry is missing when building proxy mesh.');
				return;
			}

			const highlightProxy = new THREE.Mesh(mesh.geometry, overrideMaterial || mesh.material);
			highlightProxy.matrix.copy(mesh.matrixWorld);
			highlightProxy.matrixAutoUpdate = false;
			highlightProxy.matrixWorldNeedsUpdate = true;
			highlightProxy.frustumCulled = false;
			highlightProxy.model = this;
			highlightProxy.fragId = fragId;
			highlightProxy.dbId = dbId;

			meshes.push(highlightProxy);
		});

		return meshes;
	}

	/** Clears colors for logical elements. */
	clearLogicalColor() {
		this.myData.dbId2ThemingColor.length = 0;
	}

	/** Assigns a color to a logical element. */
	setLogicalColor(dbId, color, recursive) {
		if (recursive) {
			this.getObjectTree().enumNodeChildren(dbId, (childDbId) => {
				this.myData.dbId2ThemingColor[childDbId] = color;
			}, true);
		} else {
			this.myData.dbId2ThemingColor[dbId] = color;
		}
	}

	getAnimTransformMatrix(index, dstMtx) {
		//getAnimTransform tells us whether there's an anim tranform,
		//and sets _tmp* values at the same if true
		const meshMoved = this.getFragmentList().getAnimTransform(index, _tmpScale, _tmpRot, _tmpPos);

		if (meshMoved) {
			// compose matrix from pos, rotation, and scale
			dstMtx.compose(_tmpPos, _tmpRot, _tmpScale);
		} else {
			dstMtx.identity();
		}

		return meshMoved;
	}

	// ----- Tandem added end -----
}

// Note: We don't make this an EventDispatcher, because the LMV model is one, so eventing will be handled there.
PermissionChecker.prototype.apply(DtModel.prototype);
